import type { Domain, Resort } from '@/services/inventory-api.service';
import type { SeasonalPeriod } from '@/utils/domain-season';
import type { CheckoutPassSelectionDto } from '@/services/orders.service';
import { BOOK_SKI_PASS_STORAGE_KEY } from '@/shared/constants/storage-keys';
import type { Step } from '@/types/types';
import { formatLocalDateOnly } from '@/utils/date-only';
import {
  stripDomainIdsFromReferenceData,
  stripIdsFromResortDomains,
} from '@/utils/reference-domain-activity';
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

type StepOneFormFieldsValues = {
  bookingReference: string;
  promoCode: string;
};

export type ReferenceResponseData = {
  date: string;
  duration: number;
  pax: number;
  resort: string;
  geonamesId?: number;
  domains?: Array<{ id: number; name: string }>;
  priority_domain?: { id: number; name: string };
  /** Present only if the reference API sends the Alps2Alps customer id. */
  userId?: string | number;
  user_id?: string | number;
};

/** Alps2Alps customer id. Empty values are dropped so events never send a blank userId. */
export function readAlps2AlpsUserId(data: object): string | number | null {
  const record = data as Record<string, unknown>;
  const raw = record.userId ?? record.user_id;
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return raw;
  }
  return null;
}

export type SkiersLevel = 'beginner' | 'intermediate' | 'advanced';
export type SkiStyle = 'fullDays' | 'balancedPace' | 'relaxed';

export type SkierCounts = Record<string, number>;

export type StepTwoFormFieldsValues = {
  skierCounts: SkierCounts;
  daysOfSkiing: string;
  startingDate: string;
  skiersLevel: SkiersLevel | null;
  skiStyle: SkiStyle | null;
};

export type ReferenceResortDomain = { id: number; name: string };

export type BookingSkierFormData = {
  skipassId?: number;
  firstName: string;
  lastName: string;
  birthDate: string;
};

export type StepThreeFormFieldsValues = {
  customerEmail: string;
  customerPhone: string;
  skierForms: BookingSkierFormData[];
  passSelectionOverrides: CheckoutPassSelectionDto[] | null;
};

function debugPassSyncLog(event: string, payload: Record<string, unknown>): void {
  if (typeof globalThis === 'undefined') return;
  const debugEnabled = Boolean((globalThis as { __B2C_DEBUG_PASS_SYNC?: boolean }).__B2C_DEBUG_PASS_SYNC);
  if (!debugEnabled) return;
  console.info(`[pass-sync][store] ${event}`, payload);
}

type BookSkiPassState = {
  stepOneFormFieldsValues: StepOneFormFieldsValues;
  referenceResponseData: ReferenceResponseData | null;
  /** Alps2Alps loyalty customer id. Survives a booking reset. */
  alps2alpsUserId: string | number | null;
  bookingReferenceNumber: string | null;
  stepTwoFormFieldsValues: StepTwoFormFieldsValues;
  stepThreeFormFieldsValues: StepThreeFormFieldsValues;
  hasAppliedReferenceToStepTwo: boolean;
  resorts: Resort[];
  referenceResortDomains: ReferenceResortDomain[] | null;
  selectedDomain: Domain | null;
  /** Booking reference priority domain seasons; used for date rules until user picks another ski area. */
  referenceDomainSeasonPeriods: SeasonalPeriod[] | null;
  /** True after user confirms a ski area via pre-step or "Change ski area". */
  manualSkiAreaSelection: boolean;
  selectedSkiPassOfferIndex: number | null;
  selectedPassQuantities: Record<string, number>;
  isLoading: boolean;
  error: string | null;
  currentStep: Step;
  updateStepOneFormField: (field: keyof StepOneFormFieldsValues, value: string) => void;
  updateStepTwoFormFields: (updates: Partial<StepTwoFormFieldsValues>) => void;
  setReferenceResponseData: (data: ReferenceResponseData | null) => void;
  setAlps2AlpsUserId: (userId: number | null) => void;
  setBookingReferenceNumber: (reference: string) => void;
  setStepTwoFormFieldsValues: (data: StepTwoFormFieldsValues) => void;
  setStepThreeFormFieldsValues: (data: StepThreeFormFieldsValues) => void;
  updateStepThreeFormFields: (updates: Partial<StepThreeFormFieldsValues>) => void;
  setHasAppliedReferenceToStepTwo: (value: boolean) => void;
  setResorts: (resorts: Resort[]) => void;
  setReferenceResortDomains: (domains: ReferenceResortDomain[] | null) => void;
  setSelectedDomain: (domain: Domain | null) => void;
  setReferenceDomainSeasonPeriods: (periods: SeasonalPeriod[] | null) => void;
  setManualSkiAreaSelection: (value: boolean) => void;
  captureReferenceDomainSeasonSnapshot: (fullDomain: Domain) => void;
  setSelectedSkiPassOfferIndex: (index: number | null) => void;
  setPassQuantity: (
    domainId: number,
    quantity: number,
    options: {
      passPk?: number;
      customerType?: string;
      quantityKey?: string;
      itemIndex?: number;
      source?: string;
    }
  ) => void;
  clearOffersDependentSelections: () => void;
  updateStepThreeSkierForm: (slotIndex: number, updates: Partial<BookingSkierFormData>) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setCurrentStep: (step: Step) => void;
  resetStepData: (step: Step) => void;
  reset: () => void;
  purgeReferenceDomainIds: (domainIds: number[]) => void;
};

const initialStepOneFormFieldsValues: StepOneFormFieldsValues = {
  bookingReference: '',
  promoCode: '',
};

const getDefaultStartingDate = (): string => {
  const d = new Date();
  d.setDate(d.getDate() + 2);
  return formatLocalDateOnly(d);
};

const getInitialStepTwoFormFieldsValues = (): StepTwoFormFieldsValues => ({
  skierCounts: {},
  daysOfSkiing: '8',
  startingDate: getDefaultStartingDate(),
  skiersLevel: null,
  skiStyle: 'fullDays',
});

const getInitialStepThreeFormFieldsValues = (): StepThreeFormFieldsValues => ({
  customerEmail: '',
  customerPhone: '',
  skierForms: [],
  passSelectionOverrides: null,
});

export const useBookSkiPassStore = create<BookSkiPassState>()(
  persist(
    devtools(
      (set) => ({
        stepOneFormFieldsValues: initialStepOneFormFieldsValues,
        referenceResponseData: null,
        alps2alpsUserId: null,
        bookingReferenceNumber: null,
        stepTwoFormFieldsValues: getInitialStepTwoFormFieldsValues(),
        stepThreeFormFieldsValues: getInitialStepThreeFormFieldsValues(),
        hasAppliedReferenceToStepTwo: false,
        resorts: [],
        referenceResortDomains: null,
        selectedDomain: null,
        referenceDomainSeasonPeriods: null,
        manualSkiAreaSelection: false,
        selectedSkiPassOfferIndex: null,
        selectedPassQuantities: {},
        isLoading: false,
        error: null,
        currentStep: 1,
        updateStepOneFormField: (field, value) => {
          set(
            (state) => {
              if (state.stepOneFormFieldsValues[field] === value) {
                return state;
              }

              return {
                ...state,
                stepOneFormFieldsValues: {
                  ...state.stepOneFormFieldsValues,
                  [field]: value,
                },
              };
            },
            undefined,
            `bookSkiPass/updateField/${field}`
          );
        },
        updateStepTwoFormFields: (updates) => {
          set(
            (state) => ({
              ...state,
              stepTwoFormFieldsValues: {
                ...state.stepTwoFormFieldsValues,
                ...updates,
              },
            }),
            undefined,
            'bookSkiPass/updateTripDetails'
          );
        },
        setReferenceResponseData: (data) => {
          set(
            (state) => {
              const parsed = data ? readAlps2AlpsUserId(data) : null;
              return {
                referenceResponseData: data,
                alps2alpsUserId: parsed ?? state.alps2alpsUserId,
              };
            },
            undefined,
            'bookSkiPass/setReferenceResponseData'
          );
        },
        setAlps2AlpsUserId: (userId) => {
          set(
            { alps2alpsUserId: userId },
            undefined,
            'bookSkiPass/setAlps2AlpsUserId'
          );
        },
        setBookingReferenceNumber: (reference) => {
          set(
            { bookingReferenceNumber: reference },
            undefined,
            'bookSkiPass/setBookingReferenceNumber'
          );
        },
        setStepTwoFormFieldsValues: (data) => {
          set(
            { stepTwoFormFieldsValues: data },
            undefined,
            'bookSkiPass/setStepTwoFormFieldsValues'
          );
        },
        setStepThreeFormFieldsValues: (data) => {
          set(
            { stepThreeFormFieldsValues: data },
            undefined,
            'bookSkiPass/setStepThreeFormFieldsValues'
          );
        },
        updateStepThreeFormFields: (updates) => {
          set(
            (state) => ({
              ...state,
              stepThreeFormFieldsValues: {
                ...state.stepThreeFormFieldsValues,
                ...updates,
              },
            }),
            undefined,
            'bookSkiPass/updateStepThreeFormFields'
          );
        },
        setHasAppliedReferenceToStepTwo: (value) => {
          set(
            { hasAppliedReferenceToStepTwo: value },
            undefined,
            'bookSkiPass/setHasAppliedReferenceToStepTwo'
          );
        },
        setResorts: (resorts) => {
          set({ resorts }, undefined, 'bookSkiPass/setResorts');
        },
        setReferenceResortDomains: (domains) => {
          set(
            { referenceResortDomains: domains },
            undefined,
            'bookSkiPass/setReferenceResortDomains'
          );
        },
        purgeReferenceDomainIds: (domainIds) => {
          if (domainIds.length === 0) return;
          const remove = new Set(domainIds);
          set(
            (state) => {
              const ref = state.referenceResponseData;
              const nextRef =
                ref != null ? stripDomainIdsFromReferenceData(ref, remove) : state.referenceResponseData;
              const nextResortDomains = stripIdsFromResortDomains(state.referenceResortDomains, remove);
              let nextSelected = state.selectedDomain;
              if (nextSelected != null && remove.has(nextSelected.pk)) nextSelected = null;
              let nextPeriods = state.referenceDomainSeasonPeriods;
              if (ref?.priority_domain != null && remove.has(ref.priority_domain.id)) {
                nextPeriods = null;
              }
              const nextResorts = state.resorts.map((r) => ({
                ...r,
                domains: r.domains.filter((d) => !remove.has(d.id)),
                priority_domain:
                  r.priority_domain != null && remove.has(r.priority_domain.id)
                    ? undefined
                    : r.priority_domain,
              }));
              const nextPassQuantities = Object.fromEntries(
                Object.entries(state.selectedPassQuantities).filter(([key]) => {
                  const domainPart = Number.parseInt(key.split('-')[0] ?? '', 10);
                  return !(Number.isFinite(domainPart) && remove.has(domainPart));
                })
              );
              return {
                ...state,
                referenceResponseData: nextRef,
                referenceResortDomains: nextResortDomains,
                selectedDomain: nextSelected,
                referenceDomainSeasonPeriods: nextPeriods,
                resorts: nextResorts,
                selectedPassQuantities: nextPassQuantities,
              };
            },
            undefined,
            'bookSkiPass/purgeReferenceDomainIds'
          );
        },
        setSelectedDomain: (domain) => {
          set({ selectedDomain: domain }, undefined, 'bookSkiPass/setSelectedDomain');
        },
        setReferenceDomainSeasonPeriods: (periods) => {
          set(
            { referenceDomainSeasonPeriods: periods },
            undefined,
            'bookSkiPass/setReferenceDomainSeasonPeriods'
          );
        },
        setManualSkiAreaSelection: (value) => {
          set({ manualSkiAreaSelection: value }, undefined, 'bookSkiPass/setManualSkiAreaSelection');
        },
        captureReferenceDomainSeasonSnapshot: (fullDomain) => {
          set(
            (state) => {
              if (state.manualSkiAreaSelection) return state;
              const pid = state.referenceResponseData?.priority_domain?.id;
              if (pid == null || fullDomain.pk !== pid) return state;
              const periods = Array.isArray(fullDomain.seasonal_periods)
                ? fullDomain.seasonal_periods
                : [];
              return { ...state, referenceDomainSeasonPeriods: periods };
            },
            undefined,
            'bookSkiPass/captureReferenceDomainSeasonSnapshot'
          );
        },
        setSelectedSkiPassOfferIndex: (index) => {
          set(
            { selectedSkiPassOfferIndex: index },
            undefined,
            'bookSkiPass/setSelectedSkiPassOfferIndex'
          );
        },
        setPassQuantity: (domainId, quantity, options) => {
          const { passPk, customerType, quantityKey, itemIndex, source } = options;
          const keys: string[] = [];
          const normalizedCategory = customerType?.trim().toLowerCase() ?? '';
          const hasExplicitStableKey = Boolean(quantityKey && quantityKey.trim().length > 0);
          if (quantityKey && quantityKey.trim().length > 0) {
            keys.push(quantityKey);
          }
          if (passPk != null && normalizedCategory) {
            keys.push(`${String(domainId)}-pk-${String(passPk)}-ct-${normalizedCategory}`);
          }
          if (!hasExplicitStableKey && itemIndex != null) {
            keys.push(`${String(domainId)}-idx-${String(itemIndex)}`);
            keys.push(`${String(domainId)}-${String(itemIndex)}`);
          }
          if (keys.length === 0) return;

          const uniqueKeys = [...new Set(keys)];
          const nextQuantity = Math.max(0, quantity);
          debugPassSyncLog('setPassQuantity.request', {
            source: source ?? 'unknown',
            domainId,
            passPk: passPk ?? null,
            category: normalizedCategory || null,
            itemIndex: itemIndex ?? null,
            quantity: nextQuantity,
            keys: uniqueKeys,
          });
          set(
            (state) => {
              const hasChanges = uniqueKeys.some(
                (key) => (state.selectedPassQuantities[key] ?? 0) !== nextQuantity
              );
              if (!hasChanges) {
                debugPassSyncLog('setPassQuantity.noop', {
                  source: source ?? 'unknown',
                  domainId,
                  keys: uniqueKeys,
                  quantity: nextQuantity,
                });
                return state;
              }

              const nextSelectedPassQuantities = { ...state.selectedPassQuantities };
              for (const key of uniqueKeys) {
                nextSelectedPassQuantities[key] = nextQuantity;
              }

               debugPassSyncLog('setPassQuantity.apply', {
                 source: source ?? 'unknown',
                 domainId,
                 keys: uniqueKeys,
                 quantity: nextQuantity,
               });

              return {
                ...state,
                selectedPassQuantities: nextSelectedPassQuantities,
              };
            },
            undefined,
            'bookSkiPass/setPassQuantity'
          );
        },
        clearOffersDependentSelections: () => {
          set(
            (state) => ({
              ...state,
              stepThreeFormFieldsValues: {
                ...state.stepThreeFormFieldsValues,
                skierForms: [],
              },
              selectedPassQuantities: {},
            }),
            undefined,
            'bookSkiPass/clearOffersDependentSelections'
          );
        },
        updateStepThreeSkierForm: (slotIndex, updates) => {
          set(
            (state) => {
              if (slotIndex < 0) {
                return state;
              }

              const nextForms = [...state.stepThreeFormFieldsValues.skierForms];
              while (nextForms.length <= slotIndex) {
                nextForms.push({
                  skipassId: undefined,
                  firstName: '',
                  lastName: '',
                  birthDate: '',
                });
              }

              const current = nextForms[slotIndex] ?? {
                skipassId: undefined,
                firstName: '',
                lastName: '',
                birthDate: '',
              };

              nextForms[slotIndex] = {
                skipassId: updates.skipassId ?? current.skipassId,
                firstName: updates.firstName ?? current.firstName,
                lastName: updates.lastName ?? current.lastName,
                birthDate: updates.birthDate ?? current.birthDate,
              };

              return {
                ...state,
                stepThreeFormFieldsValues: {
                  ...state.stepThreeFormFieldsValues,
                  skierForms: nextForms,
                },
              };
            },
            undefined,
            'bookSkiPass/updateStepThreeSkierForm'
          );
        },
        setLoading: (loading) => {
          set({ isLoading: loading }, undefined, 'bookSkiPass/setLoading');
        },
        setError: (error) => {
          set({ error }, undefined, 'bookSkiPass/setError');
        },
        setCurrentStep: (step: Step) => {
          set({ currentStep: step }, undefined, 'bookSkiPass/setCurrentStep');
        },
        resetStepData: (step) => {
          set(
            (state) => {
              if (step === 2) {
                return {
                  ...state,
                  stepTwoFormFieldsValues: getInitialStepTwoFormFieldsValues(),
                  hasAppliedReferenceToStepTwo: false,
                  resorts: [],
                  referenceResortDomains: null,
                  selectedDomain: null,
                  referenceDomainSeasonPeriods: null,
                  manualSkiAreaSelection: false,
                  selectedSkiPassOfferIndex: null,
                  selectedPassQuantities: {},
                  stepThreeFormFieldsValues: getInitialStepThreeFormFieldsValues(),
                  isLoading: false,
                  error: null,
                };
              }

              if (step === 1) {
                return {
                  ...state,
                  stepOneFormFieldsValues: initialStepOneFormFieldsValues,
                  referenceResponseData: null,
                  bookingReferenceNumber: null,
                  stepTwoFormFieldsValues: getInitialStepTwoFormFieldsValues(),
                  stepThreeFormFieldsValues: getInitialStepThreeFormFieldsValues(),
                  hasAppliedReferenceToStepTwo: false,
                  resorts: [],
                  referenceResortDomains: null,
                  selectedDomain: null,
                  referenceDomainSeasonPeriods: null,
                  manualSkiAreaSelection: false,
                  selectedSkiPassOfferIndex: null,
                  selectedPassQuantities: {},
                  isLoading: false,
                  error: null,
                };
              }

              return state;
            },
            undefined,
            'bookSkiPass/resetStepData'
          );
        },
        reset: () => {
          set(
            {
              stepOneFormFieldsValues: initialStepOneFormFieldsValues,
              referenceResponseData: null,
              bookingReferenceNumber: null,
              stepTwoFormFieldsValues: getInitialStepTwoFormFieldsValues(),
              stepThreeFormFieldsValues: getInitialStepThreeFormFieldsValues(),
              hasAppliedReferenceToStepTwo: false,
              resorts: [],
              referenceResortDomains: null,
              selectedDomain: null,
              referenceDomainSeasonPeriods: null,
              manualSkiAreaSelection: false,
              selectedSkiPassOfferIndex: null,
              selectedPassQuantities: {},
              isLoading: false,
              error: null,
              currentStep: 1,
            },
            undefined,
            'bookSkiPass/reset'
          );
        },
      }),
      { name: 'book-ski-pass-store' }
    ),
    { name: BOOK_SKI_PASS_STORAGE_KEY }
  )
);
